import { useMemo } from 'react';

import {
  REST_FALLBACK_SEC,
  estimateLiveWorkoutCalories,
  resolveAthleteEnergy,
  resolveWorkoutCalories,
  type EffortScale,
  type MovementPattern,
  type ResolvedWorkoutCalories,
} from '@heraion/shared';

import type { LoggerDraft } from './state/loggerDraftTypes';
import type { LoggerProfile } from './hostDeps';
import { buildPayload } from './workoutPayload';

type LiveEnergyPayload = Extract<
  ReturnType<typeof buildPayload>,
  { ok: true }
>['payload'];

const countSets = (p: {
  entries: ReadonlyArray<{ sets: ReadonlyArray<unknown> }>;
}) => p.entries.reduce((n, e) => n + e.sets.length, 0);

// The inputs the live readout needs from ONE parse of the draft: the payload
// the estimator runs on (dropUncompleted: false — it counts the set not yet
// marked done), plus whether that count is a PROJECTION rather than a
// measurement (LG-46). It IS a projection exactly when dropping the uncompleted
// sets — which is what the SAVE path does (dropUncompleted: true) — would change
// the counted set list. Comparing the two builds is the honest signal: it
// tracks whatever buildPayload actually keeps (a blank proposed set is dropped
// by BOTH builds and is not a projection), never a re-derived guess. Built once
// here so the hot logging path parses the draft twice, not three times (the
// estimator and the projection check used to each rebuild the full payload).
// Antenore 2026-08-25: the math stays; only the (i) provenance has to SAY it's
// a projection.
export function buildLiveEnergyInputs(
  draft: LoggerDraft,
  effortScale: EffortScale,
): { payload: LiveEnergyPayload; projected: boolean } | null {
  const full = buildPayload(draft, effortScale, { dropUncompleted: false });
  if (!full.ok) return null;
  const saved = buildPayload(draft, effortScale, { dropUncompleted: true });
  const projected =
    !saved.ok || countSets(full.payload) > countSets(saved.payload);
  return { payload: full.payload, projected };
}

// HC-8 — the LIVE workout energy readout for the logger footer. There's no
// endedAt mid-workout, so estimateLiveWorkoutCalories PROJECTS the duration from
// the set structure (reps + rest) and reuses the save-path estimator; it
// re-projects as sets/exercises change. Only the estimate exists live (HC
// enriches the measured value later), so the resolver just wraps it — the
// measured-vs-estimate case is a saved, enriched session (B2c), handled by
// useWorkoutEnergyReadout, not this live path. `hcWins` is passed in (read once
// by the caller) rather than subscribed here, so the two readout paths don't
// each register their own preference listener.
export function useLiveWorkoutEnergy(
  draft: LoggerDraft,
  effortScale: EffortScale,
  profile: LoggerProfile,
  hcWins: boolean,
): {
  resolved: ResolvedWorkoutCalories;
  projected: boolean;
  metFallbackKcal: number | null;
} | null {
  return useMemo(() => {
    const inputs = buildLiveEnergyInputs(draft, effortScale);
    if (!inputs) return null;
    const patterns: Record<string, MovementPattern | null> = {};
    for (const e of draft.entries) patterns[e.exerciseId] = e.movementPattern;
    const athlete = resolveAthleteEnergy(profile);
    const restSec = profile.defaultRestSec ?? REST_FALLBACK_SEC;
    const estimate = estimateLiveWorkoutCalories(
      inputs.payload,
      patterns,
      athlete,
      restSec,
    );
    if (!estimate) return null;
    const resolved = resolveWorkoutCalories(
      { caloriesEstimate: estimate },
      { hcWins },
    );
    if (!resolved) return null;
    // LEGAL-5 ripiego (2026-09-09): a `keytel` live estimate is HR-derived
    // (HC), so recompute the SAME estimator with HR stripped for the share
    // card's fallback. Already `met` ⇒ it IS its own fallback, no second call.
    const metFallbackKcal =
      estimate.method === 'met'
        ? estimate.kcal
        : (estimateLiveWorkoutCalories(inputs.payload, patterns, athlete, restSec, {
            includeHr: false,
          })?.kcal ?? null);
    return { resolved, projected: inputs.projected, metFallbackKcal };
  }, [draft, effortScale, profile, hcWins]);
}
